/**
 * Machine-spike types for the centralised Remix state model.
 *
 * This is the state-machine-based iteration of `../types.ts`, produced from the
 * review feedback on the initial store PR (Loom + Lauren):
 *
 * 1. **Discriminated union for create vs edit** (Loom feedback #3). Instead of
 *    one `RemixActionInstance` with a `kind` field and a pile of optional
 *    edit-only fields, we model `RemixCreateActionInstance` and
 *    `RemixEditActionInstance` as separate types. Edit-only context is then
 *    *required* on the edit type and *absent* on the create type, so the
 *    compiler enforces "these fields only exist for edits".
 *
 * 2. **No derived booleans in stored state** (Loom feedback #1). The phase is
 *    the single source of truth for lifecycle; we do not also store
 *    `isComplete` / `isError` / `isAborted`. Those are read off the phase.
 *
 * 3. **Explicit `targetNodeId`** (PR comment + Lauren's editor-loading
 *    requirement). The node identity (mediaId / contentMauiId) of the content a
 *    remix is anchored to is promoted to a first-class field, rather than being
 *    buried inside the opaque `historyEntry`. Cross-store effects (e.g. the
 *    editor inline/embed loading state) need this to target the right node.
 *
 *    Named `targetNodeId`, NOT `targetContentId`: in Confluence `contentId`
 *    conventionally means the *page* id, whereas this is a node identity within
 *    the page. It is required for edits and optional for creates (an anchored
 *    create — remixing a selected node — has one; an unanchored create does
 *    not).
 *
 * These types live under `machine/` alongside the parallel machine
 * implementation and do NOT replace `../types.ts` yet — both coexist during the
 * spike.
 */

import type { RemixMetadata, RemixPhase } from '../types';

export type { RemixId, RemixPhase, RemixMetadata } from '../types';

/**
 * Fields shared by every remix flow instance regardless of create/edit.
 *
 * Note there is deliberately no derived boolean state here — `phase` is
 * authoritative. Anything computable from the conversation messages
 * (complete / error / aborted / generated content) is derived, not stored.
 */
interface RemixInstanceBase {
	/** Unique id for this flow. Key into the store map. */
	id: string;
	/** Current lifecycle phase (single source of truth for lifecycle). */
	phase: RemixPhase;
	/**
	 * The Conversation Assistant conversation backing this flow.
	 *
	 * Per Lauren's review: keeping the `conversationId` at the top level of the
	 * instance (rather than only a derived final-message id) makes the state
	 * debuggable — you can log the instance and look the conversation up — and
	 * lets the watcher derive the final message id from the conversation instead
	 * of us storing it.
	 */
	conversationId: string;
	/** What kind of remix this is (drives UI + backend payload). */
	metadata: RemixMetadata;
	/** Millisecond timestamp when the flow started (analytics / ordering). */
	startedAt: number;
}

/**
 * A brand-new remix flow. Carries no edit-only context (no sentinel, no saved
 * selection, no history entry).
 *
 * It MAY still be anchored: remixing a selected node in the editor is a create,
 * but it has a node to point the inline/embed loading state at. Anchoring is
 * therefore optional here and required on the edit type.
 */
export interface RemixCreateActionInstance extends RemixInstanceBase {
	kind: 'create';
	/**
	 * Optional anchor node (mediaId / contentMauiId) this create relates to.
	 * Present for an anchored create (remix of an existing node), absent when
	 * generating content with nowhere to point yet.
	 */
	targetNodeId?: string;
}

/**
 * An edit of an already-inserted remix. Carries the extra context that only
 * makes sense for edits — all required, because if we are editing then by
 * definition we know what we are editing.
 */
export interface RemixEditActionInstance extends RemixInstanceBase {
	kind: 'update';
	/**
	 * The node identity (mediaId / contentMauiId) of the already-inserted remix
	 * being edited. Required for edits: it is how we (a) insert the new variant
	 * back into the correct node, (b) detect mid-edit deletion of the target
	 * (MAUI-975), and (c) drive the editor's inline/embed loading state for the
	 * right node (Lauren's cross-store requirement).
	 */
	targetNodeId: string;
	/**
	 * Opaque snapshot of the editor selection captured when the edit started,
	 * used to restore selection on cancel. Opaque to avoid an editor dependency.
	 */
	savedSelection: unknown;
	/**
	 * Opaque snapshot of the variant-history entry being acted upon. Opaque to
	 * avoid a dependency on the editor history plugin types.
	 */
	historyEntry: unknown;
}

/**
 * The union type of any remix flow instance. Discriminated on `kind`.
 */
export type RemixInstance = RemixCreateActionInstance | RemixEditActionInstance;

/** Narrowing helper: is this instance an edit? */
export const isEditInstance = (instance: RemixInstance): instance is RemixEditActionInstance =>
	instance.kind === 'update';

/** Narrowing helper: is this instance a create? */
export const isCreateInstance = (instance: RemixInstance): instance is RemixCreateActionInstance =>
	instance.kind === 'create';

/**
 * Phases that mean the flow is finished and the instance should leave the map.
 *
 * This is the SINGLE source of truth for terminality. Do not re-declare a local
 * copy (array or Set) elsewhere: a duplicate silently diverges the moment a new
 * terminal phase is added here, causing `isActivePhase` / `useIsAnyRemixing` to
 * misclassify finished flows as active. Import `isActivePhase` instead.
 */
export const TERMINAL_PHASES: readonly RemixPhase[] = ['error', 'aborted'];

/**
 * `TERMINAL_PHASES` as a Set, for O(1) membership checks on hot paths (the
 * `useSyncExternalStore` snapshot readers call this on every render).
 *
 * Derived from `TERMINAL_PHASES` rather than written out again, so adding a
 * phase to the array above automatically applies everywhere.
 */
const TERMINAL_PHASE_SET: ReadonlySet<RemixPhase> = new Set(TERMINAL_PHASES);

/**
 * Whether a phase represents an active (non-terminal) flow. `inserting` is still
 * active; the instance is removed once insertion completes (via an explicit
 * event), and `error`/`aborted` are terminal.
 */
export const isActivePhase = (phase: RemixPhase): boolean => !TERMINAL_PHASE_SET.has(phase);
